import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {WoocommerceSalesChannel} from '@/types/salesChannels/woocommerceSalesChannels';
import type {DelegationApiKeyCredentials, PostRegisterApiKeyResponse} from '@myparcel-frontend/delegation-service';
import type {
  CreateSalesChannelRequest,
  ImportProductsRequest,
  PostRequestDelegationResponse,
  RenameSalesChannelRequest,
} from '@myparcel-frontend/woo-ecommerce-service';
import {useDelegationService} from '@myparcel-frontend/delegation-service';
import {useWooEcommerceService} from '@myparcel-frontend/woo-ecommerce-service';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import logToMonitor from '@/utils/logging';

export default function useWoocommerceEcommerceSalesChannels() {
  const salesChannelStore = useSalesChannelStore();

  const getWoocommerceSalesChannels = async (shopIds: string[]): Promise<WoocommerceSalesChannel[] | []> => {
    const client = useWooEcommerceService();

    try {
      const response = await client.getSalesChannels(shopIds);
      const result: WoocommerceSalesChannel[] = [];

      if (!response || !response?.results?.length) {
        return result;
      }

      for (const salesChannel of response.results) {
        const woocommerceSalesChannel: WoocommerceSalesChannel = {
          ...salesChannel,
          ecommercePlatform: 'WOOCOMMERCE',
        };

        const newSalesChannel = salesChannelStore.addSalesChannel(woocommerceSalesChannel);

        result.push(newSalesChannel);
      }

      return result;
    } catch (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching WooCommerce sales channels failed in useWoocommerce composable',
      });

      throw error;
    }
  };

  const createWoocommerceSalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.createSalesChannel(data);

      const newWoocommerceSalesChannel: WoocommerceSalesChannel = {
        ...response,
        ecommercePlatform: 'WOOCOMMERCE',
      };

      salesChannelStore.addSalesChannel(newWoocommerceSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating WooCommerce sales channel failed in useWoocommerce composable',
        _myparcel_sales_channel_payload: data,
      });

      throw error;
    }
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.renameSalesChannel(data);

      const updatedWoocommerceSalesChannel: WoocommerceSalesChannel = {
        ...response,
        ecommercePlatform: 'WOOCOMMERCE',
      };

      salesChannelStore.updateSalesChannel(updatedWoocommerceSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Renaming WooCommerce sales channel failed in useWoocommerce composable',
        _myparcel_sales_channel_id: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }
  };

  const removeWoocommerceSalesChannel = async (salesChannelId: string) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.removeSalesChannel(salesChannelId);

      salesChannelStore.removeSalesChannel(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing WooCommerce sales channel failed in useWoocommerce composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const enableOrderLoading = async (salesChannelId: string) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.enableOrderLoading(salesChannelId);

      salesChannelStore.enableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for WooCommerce sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }
  };

  const disableOrderLoading = async (salesChannelId: string) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.disableOrderLoading(salesChannelId);

      salesChannelStore.disableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for WooCommerce sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }
  };

  const fetchWoocommerceSalesChannelById = async (
    salesChannelId: string,
  ): Promise<WoocommerceSalesChannel | undefined> => {
    const client = useWooEcommerceService();

    try {
      const response = await client.getSalesChannelById(salesChannelId);

      const woocommerceSalesChannel: WoocommerceSalesChannel = {
        ...response,
        ecommercePlatform: 'WOOCOMMERCE',
      };

      salesChannelStore.addSalesChannel(woocommerceSalesChannel);

      return woocommerceSalesChannel;
    } catch (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching WooCommerce sales channel by id failed',
        _myparcel_sales_channel: salesChannelId,
      });

      throw error;
    }
  };

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse | undefined> => {
    const client = useWooEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
      authorizeUrl: response.authorizeUrl,
    });

    return response;
  };

  const createApiKeyCredentials = async (
    salesChannelId: EcommerceSalesChannel['id'],
    credentials: DelegationApiKeyCredentials,
  ): Promise<PostRegisterApiKeyResponse> => {
    const client = useDelegationService();

    const result = await client.createDelegationRequestsApiKey({
      apiKey: credentials,
      delegateeId: {
        delegatee: 'WOOCOMMERCE',
        id: salesChannelId,
      },
    });

    return result;
  };

  const importWoocommerceProducts = async (saleschannelIds: ImportProductsRequest['salesChannelIds']) => {
    const client = useWooEcommerceService();

    try {
      const response = await client.importProducts(saleschannelIds);
      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Importing WooCommerce products failed in useWoocommerce composable',
        _myparcel_sales_channel_ids: saleschannelIds,
      });
      throw error;
    }
  };

  return {
    getWoocommerceSalesChannels,
    createWoocommerceSalesChannel,
    removeWoocommerceSalesChannel,
    renameSalesChannel,
    enableOrderLoading,
    disableOrderLoading,
    requestDelegation,
    createApiKeyCredentials,
    fetchWoocommerceSalesChannelById,
    importWoocommerceProducts,
  };
}
